// Simple Analytics Tracking for ONTAP Global
// Uses localStorage for MVP - can be upgraded to backend later

const ANALYTICS_KEY = 'ontap_analytics';

// Get current analytics data
const getAnalytics = () => {
  try {
    const data = localStorage.getItem(ANALYTICS_KEY);
    return data ? JSON.parse(data) : initializeAnalytics();
  } catch {
    return initializeAnalytics();
  }
};

// Initialize empty analytics structure
const initializeAnalytics = () => ({
  pageViews: {},
  communityViews: {},
  whatsappClicks: {},
  joinClicks: {},
  featureClicks: {},
  sessions: [],
  lastUpdated: new Date().toISOString()
});

// Save analytics data
const saveAnalytics = (data) => {
  data.lastUpdated = new Date().toISOString();
  localStorage.setItem(ANALYTICS_KEY, JSON.stringify(data));
};

// Track a page view
export const trackPageView = (pageName) => {
  const analytics = getAnalytics();
  const today = new Date().toISOString().split('T')[0];

  if (!analytics.pageViews[pageName]) {
    analytics.pageViews[pageName] = {};
  }
  if (!analytics.pageViews[pageName][today]) {
    analytics.pageViews[pageName][today] = 0;
  }
  analytics.pageViews[pageName][today]++;

  saveAnalytics(analytics);
  console.log(`📊 Page view tracked: ${pageName}`);
};

// Track community page view
export const trackCommunityView = (communityId) => {
  const analytics = getAnalytics();
  const today = new Date().toISOString().split('T')[0];
  const timestamp = new Date().toISOString();

  if (!analytics.communityViews[communityId]) {
    analytics.communityViews[communityId] = {
      total: 0,
      daily: {},
      history: []
    };
  }

  analytics.communityViews[communityId].total++;

  if (!analytics.communityViews[communityId].daily[today]) {
    analytics.communityViews[communityId].daily[today] = 0;
  }
  analytics.communityViews[communityId].daily[today]++;

  analytics.communityViews[communityId].history.push(timestamp);
  // Keep only last 100 entries
  if (analytics.communityViews[communityId].history.length > 100) {
    analytics.communityViews[communityId].history =
      analytics.communityViews[communityId].history.slice(-100);
  }

  saveAnalytics(analytics);
  console.log(`📊 Community view tracked: ${communityId}`);
};

// Track WhatsApp button click
export const trackWhatsAppClick = (communityId) => {
  const analytics = getAnalytics();
  const today = new Date().toISOString().split('T')[0];
  const timestamp = new Date().toISOString();

  if (!analytics.whatsappClicks[communityId]) {
    analytics.whatsappClicks[communityId] = {
      total: 0,
      daily: {},
      history: []
    };
  }

  analytics.whatsappClicks[communityId].total++;

  if (!analytics.whatsappClicks[communityId].daily[today]) {
    analytics.whatsappClicks[communityId].daily[today] = 0;
  }
  analytics.whatsappClicks[communityId].daily[today]++;

  analytics.whatsappClicks[communityId].history.push(timestamp);
  if (analytics.whatsappClicks[communityId].history.length > 100) {
    analytics.whatsappClicks[communityId].history =
      analytics.whatsappClicks[communityId].history.slice(-100);
  }

  saveAnalytics(analytics);
  console.log(`📊 WhatsApp click tracked: ${communityId}`);
};

// Track Join button click
export const trackJoinClick = (communityId) => {
  const analytics = getAnalytics();
  const today = new Date().toISOString().split('T')[0];

  if (!analytics.joinClicks[communityId]) {
    analytics.joinClicks[communityId] = {
      total: 0,
      daily: {}
    };
  }

  analytics.joinClicks[communityId].total++;

  if (!analytics.joinClicks[communityId].daily[today]) {
    analytics.joinClicks[communityId].daily[today] = 0;
  }
  analytics.joinClicks[communityId].daily[today]++;

  saveAnalytics(analytics);
  console.log(`📊 Join click tracked: ${communityId}`);
};

// Track feature click (Rides, Tutoring, Marketplace, Connect)
export const trackFeatureClick = (featureName, fromCommunity = null) => {
  const analytics = getAnalytics();
  const today = new Date().toISOString().split('T')[0];

  if (!analytics.featureClicks[featureName]) {
    analytics.featureClicks[featureName] = {
      total: 0,
      daily: {},
      fromCommunities: {}
    };
  }

  analytics.featureClicks[featureName].total++;

  if (!analytics.featureClicks[featureName].daily[today]) {
    analytics.featureClicks[featureName].daily[today] = 0;
  }
  analytics.featureClicks[featureName].daily[today]++;

  if (fromCommunity) {
    if (!analytics.featureClicks[featureName].fromCommunities[fromCommunity]) {
      analytics.featureClicks[featureName].fromCommunities[fromCommunity] = 0;
    }
    analytics.featureClicks[featureName].fromCommunities[fromCommunity]++;
  }

  saveAnalytics(analytics);
  console.log(`📊 Feature click tracked: ${featureName}${fromCommunity ? ` from ${fromCommunity}` : ''}`);
};

// Get analytics summary
export const getAnalyticsSummary = () => {
  const analytics = getAnalytics();

  // Calculate top communities by views
  const communityRankings = Object.entries(analytics.communityViews || {})
    .map(([id, data]) => ({ id, views: data.total, whatsappClicks: analytics.whatsappClicks[id]?.total || 0 }))
    .sort((a, b) => b.views - a.views);

  // Calculate top features
  const featureRankings = Object.entries(analytics.featureClicks || {})
    .map(([name, data]) => ({ name, clicks: data.total }))
    .sort((a, b) => b.clicks - a.clicks);

  // Total stats
  const totalCommunityViews = Object.values(analytics.communityViews || {})
    .reduce((sum, data) => sum + data.total, 0);

  const totalWhatsAppClicks = Object.values(analytics.whatsappClicks || {})
    .reduce((sum, data) => sum + data.total, 0);

  return {
    totalCommunityViews,
    totalWhatsAppClicks,
    communityRankings,
    featureRankings,
    conversionRate: totalCommunityViews > 0
      ? ((totalWhatsAppClicks / totalCommunityViews) * 100).toFixed(1)
      : 0,
    lastUpdated: analytics.lastUpdated
  };
};

// Export raw analytics for dashboard
export const getRawAnalytics = () => getAnalytics();

// Clear analytics (for testing)
export const clearAnalytics = () => {
  localStorage.removeItem(ANALYTICS_KEY);
  console.log('📊 Analytics cleared');
};

export default {
  trackPageView,
  trackCommunityView,
  trackWhatsAppClick,
  trackJoinClick,
  trackFeatureClick,
  getAnalyticsSummary,
  getRawAnalytics,
  clearAnalytics
};
